import { isProxy, toRaw } from "vue";

import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { DateTime } from "luxon";
import { webinars, syncState } from "@/plugins/LocalForagePlugin";
import { webinarsSyncKey } from "@/types/products/product-definitions";
import type {
    ArticleMetaData,
    Author,
    Category,
    ContentfulWebinar,
    HasMetaData,
    IndustrySector,
    ContentfulSector,
    Product,
    SearchableTicker,
    Tag,
    Webinar,
    WebinarMetaData
} from "@tmi/types/entities";

import type { Webinars } from "@tmi/types/api/content-models";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import { CompositionContext } from "@/services/data/sync-supervisor";

export class WebinarsSyncCoordinator {
    readonly #compositionContext: CompositionContext;

    constructor( ctx: CompositionContext ) {
        this.#compositionContext = ctx;
    }

    public async patchRepository( authToken: string ): Promise< number > {
        let itemsUpdated = 0;

        try {
            const syncThresholdInMillis = await syncState.getItem< number >( webinarsSyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() )
            {

                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( webinarsSyncKey, nextSyncThresholdInMillis );

                logger.debug( `[ webinars-sync-coordinator ] load all available webinar meta-data...` );
                const t0: number = performance.now();

                // Retrieve & store the latest meta-data for webinars
                const allMetadata = await this.refreshMetaDataFromServer( authToken );

                if(0 === allMetadata.length)
                {
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.setItem( webinarsSyncKey, nextSyncThresholdInMillis );

                    return itemsUpdated;
                }

                const t1: number = performance.now();
                logger.info( `[ webinars-sync-coordinator ] Retrieved & loaded ${ allMetadata.length } article meta records in ${ t1 - t0 } ms` );

                const allMetaDataByIdMap = new Map< string, ArticleMetaData >(
                    allMetadata.map( ( meta: HasMetaData ) => [ meta.contentfulId, meta ] )
                );

                // Manage all cached webinars
                const t2: number = performance.now();

                let numberContentItems = await webinars.length();
                if ( numberContentItems > 0 ) {
                    // Iterate over all elements within IDB
                    await webinars.iterate( ( content: Webinar, key: string ) => {
                        // If the webinars in IDB matches one in the meta-data repository
                        const meta: WebinarMetaData | undefined = allMetaDataByIdMap.get( key );
                        // If the meta-data collection has the same webinar content item that shares the same contentful id...
                        if ( typeof meta !== 'undefined' ) {
                            // If the updated date for both the meta-data and the local webinar are the same...
                            if ( 0 === meta.updatedDate?.localeCompare( content.updatedDate ?? '' ) ) {
                                // Remove it from the list of available meta-data items
                                allMetaDataByIdMap.delete( key );
                            }
                            // Otherwise, the local content item is out of date and should be replaced
                            else {
                                // Delete the content item from the IDB collection
                                webinars.removeItem( key );
                            }
                        }
                        // Else, the current meta-data no longer contains the same content item and thus it should be removed from IDB
                        else {
                            // Delete the content item from the IDB collection
                            webinars.removeItem( key );
                        }
                        return;
                    } );
                }

                const t3: number = performance.now();
                logger.info( `[ webinars-sync-coordinator ] Aggregated missing webinars in ${ t3 - t2 } ms` );

                // Transform metadata map into an array of content ids
                const missingIds = [ ...Array.from( allMetaDataByIdMap.keys() ) ];
                // If any content is missing...
                if ( missingIds.length > 0 ) {
                    // Fetch it from the server
                    itemsUpdated = await this.refreshContentFromServer( authToken, missingIds );
                }

                const t4: number = performance.now();
                logger.info( `[ webinars-sync-coordinator ] Processing all missing webinars took ${ t4 - t0 } ms` );
            }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
        }

        return itemsUpdated;
    }

    private async refreshMetaDataFromServer( authToken: string ): Promise< WebinarMetaData[] > {
        let page: number = 0;
        const pageSize: number = 5000;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        const metadata: WebinarMetaData[] | undefined = [];
        let metaPayload: ContentfulWebinar[] | undefined = [];

        do {
            const response =
                await goFetch< Webinars, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '3a47c3a294c1f35253d45a0df29a4fb5',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                fromPublishedDateTimeUTC: '2008-01-01'
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Webinar metadata fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping webinar metadata pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            metaPayload = response?.data?.webinarCatalog?.webinars;
            if ( metaPayload ) {
                metaPayload.map( ( a: ContentfulWebinar ) => {
                    metadata.push( ( {
                        contentfulId: a.id,
                        slug: a.slug,
                        updatedDate: a.publish_at,
                    } ) );
                } );
            }

            // update the page #
            page++;
        } while ( (metaPayload?.length ?? 0) >= pageSize );

        return metadata;
    }

    private async refreshContentFromServer( authToken: string, missingContentIds: string[] ): Promise< number > {
        // Timing - testing to see how expensive each method is
        const t0: number = performance.now();
        let itemsUpdated = 0;

        let page: number = 0;
        const pageSize: number = 1000;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        let contentPayload: ContentfulWebinar[] | undefined = [];
        do {
            const response =
                await goFetch< Webinars, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '61a211b1eb8a5b94eb24bbcb79236ce6',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                ids: missingContentIds
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Webinar content fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping webinar content pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            contentPayload = response?.data?.webinarCatalog?.webinars;
            if ( null != contentPayload ) {
                const allWebinars: Webinar[] = [];
                contentPayload.map( ( w: ContentfulWebinar ) => {
                    const webinar: Webinar | undefined = composeWebinar( w, this.#compositionContext );
                    if ( typeof webinar !== 'undefined' ) {
                        allWebinars.push( webinar )
                    }
                } );

                // Store the items recently retrieved
                await webinars.ready();
                try {
                    for ( const origWebinar of allWebinars ) {
                        await webinars.setItem( origWebinar.contentfulId, structuredClone( origWebinar ) );
                    }
                } catch ( err: any ) {
                    logger.error( JSON.stringify( err.message ) )
                }

                const t1: number = performance.now();
                logger.info( `[ webinars-sync-coordinator ] Processing ${ allWebinars.length } webinars took ${ t1 - t0 } ms` );

                itemsUpdated += allWebinars.length;
            }

            // update the page #
            page++;
        } while ( (contentPayload?.length ?? 0) >= pageSize );

        return itemsUpdated;
    }
}

export function composeWebinar( webinar: ContentfulWebinar, ctx: CompositionContext ): Webinar | undefined {
    let hydratedWebinar = {} as Webinar;
    try {
        // Author
        let author: Author | undefined = ctx.getAuthorById( webinar.author_id );
        if ( author ) {
            let authorObject = author;
            if ( isProxy( author ) ) {
                authorObject = toRaw( author );
            }

            author = structuredClone( authorObject );
        }

        // Ad Product
        const adProduct: Product | undefined = !!( webinar.ad_product_id ) ?
            ctx.getProductById( webinar.ad_product_id ) : undefined;

        // Categories
        const categories: Category[] = [];
        webinar.assoc_category_ids?.forEach( ( id: string ) => {
            const category: Category | undefined = ctx.getCategoryById( id );

            if ( category ) {
                let categoryObject = category;
                if ( isProxy( category ) ) {
                    categoryObject = toRaw( category );
                }
                categories.push( structuredClone( categoryObject ) );
            }
        } );

        // Industry Sectors
        const industrySectors: IndustrySector[] = [];
        webinar.assoc_ind_sector_ids?.forEach( ( id: string ) => {
            const industrySector: IndustrySector | undefined = ctx.getIndustrySectorById( id );

            if ( industrySector ) {
                let industrySectorObject = industrySector;
                if ( isProxy( industrySector ) ) {
                    industrySectorObject = toRaw( industrySector );
                }

                industrySectors.push( structuredClone( industrySectorObject ) );
            }
        } );

        // Products
        const products: Product[] = [];
        webinar.assoc_product_ids?.forEach( ( id: string ) => {
            const product: Product | undefined = ctx.getProductById( id );

            if ( product ) {
                let productObject = product;
                if ( isProxy( product ) ) {
                    productObject = toRaw( product );
                }

                products.push( structuredClone( productObject ) );
            } else {
                logger.warn( `The given product id '${ id }' was not found to match a known Product content item.` );
            }
        } );

        // Sectors
        const sectors: ContentfulSector[] = [];
        webinar.assoc_sector_ids?.forEach( ( id: string ): void => {
            const sector: ContentfulSector | undefined = ctx.getSectorById( id );

            if ( sector ) {
                let sectorObject = sector;
                if ( isProxy( sector ) ) {
                    sectorObject = toRaw( sector );
                }

                sectors.push( structuredClone( sectorObject ) );
            }
        } );

        // Tags
        const tags: Tag[] = [];
        webinar.assoc_tag_ids?.forEach( ( id: string ): void => {
            const tag: Tag | undefined = ctx.getTagById( id );

            if ( tag ) {
                let tagObject = tag;
                if ( isProxy( tag ) ) {
                    tagObject = toRaw( tag );
                }

                tags.push( structuredClone( tagObject ) );
            }
        } );

        // Tickers
        const tickers: SearchableTicker[] = [];
        webinar.assoc_ticker_ids?.forEach( ( id: string ): void => {
            const ticker: SearchableTicker | undefined = ctx.getTickerById( id );

            if ( ticker ) {
                let tickerObject = ticker;
                if ( isProxy( ticker ) ) {
                    tickerObject = toRaw( ticker );
                }

                tickers.push( structuredClone( tickerObject ) );
            }
        } );

        hydratedWebinar = ( {
            contentfulId: webinar.id,
            webinarDate: webinar.publish_dt,
            publishedDate: webinar.publish_dt,

            updatedDate: webinar.publish_at,

            slug: webinar.slug,
            title: webinar.title,
            text: webinar.body,

            isFeatured: webinar.featured,

            editorialAd: webinar.editorial_ad ?? undefined,
            adProduct: !!( adProduct ) ? structuredClone( adProduct ) : undefined,

            registrationUri: webinar.reg_link,
            videoLink: webinar.video,

            author: structuredClone( author ),

            categories: categories,
            industrySectors: industrySectors,
            products: products,
            sectors: sectors,
            tags: tags,
            tickers: tickers,

            // Extraneous data attributes used to consolidate the very similar models
            body2: undefined,
            preview: undefined,
            thumbnail: undefined
        } );
    } catch ( e: any ) {
        logger.error( JSON.stringify( e.message ) );
    }

    return hydratedWebinar;
}